import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ForumIcon from '@mui/icons-material/Forum';
import GroupIcon from '@mui/icons-material/Group';
import HelpIcon from '@mui/icons-material/Help';
import SettingsIcon from '@mui/icons-material/Settings';
import Icon from '@mui/material/Icon';
import List from '@mui/material/List';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setSidebar } from '../../../store/sidebarDrawer';
import { DrawerComponents } from '../PageLayout';
import NavListButton from './NavListButton/NavListButton';
import NavListLink from './NavListLink/NavListLink';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        height: '100%',
        backgroundColor: theme.palette.pageNavigationBar.main,
    },
    navLink: {
        height: '100%',
        width: '100%',
    },
    icon: {
        height: '100%',
        width: '100%',
    },
    imgLogo: {
        height: '100%',
        width: '100%',
    },
}));

const navButtons: Array<{
    name: string;
    value: string;
    icon: React.ReactElement;
}> = [
    {
        name: 'New/Recent',
        value: 'new/Recent',
        icon: <ForumIcon />,
    },
    {
        name: 'Groups',
        value: 'groups',
        icon: <GroupIcon />,
    },
    {
        name: 'Campaigns',
        value: 'campaigns',
        icon: <AutoGraphIcon />,
    },
    {
        name: 'Settings',
        value: 'settings',
        icon: <SettingsIcon />,
    },
    {
        name: 'Help',
        value: 'help',
        icon: <HelpIcon />,
    },
];

interface Props {
    drawerComponent: DrawerComponents | null;
    hasLargeScreen: boolean;
}

export default function PageNavigation({ drawerComponent, hasLargeScreen }: Props) {
    const location = useLocation();
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const clientHasEngagePlatform = useAppSelector(state => state.user.clientHasEngagePlatform);

    React.useEffect(() => {
        /*
         *  Watch for location changes while using smaller screens where sidebar drawer has width 100%;
         *  close if navigating to a new page from the sidebar.
         *
         *  TIP: watch for location changes here to avoid subscribing to location changes in PageLayout;
         *  bigger renders if so.
         */
        if (!hasLargeScreen && !!drawerComponent) {
            dispatch(setSidebar({ active: null }));
        }
    }, [location]);

    const handleClick = (value: DrawerComponents) => {
        if (!value || value === drawerComponent) {
            (value as DrawerComponents | null) = null;
        }

        dispatch(setSidebar({ active: value }));
    };

    return (
        <List component="nav" disablePadding className={classes.root}>
            <NavListLink navTarget="/" primaryColor={true} className={classes.navLink}>
                <Tooltip title="Home" placement="bottom-end" disableInteractive>
                    <Icon className={classes.icon}>
                        <img className={classes.imgLogo} src="/icon_dark_no_bg.svg" alt="Logo" />
                    </Icon>
                </Tooltip>
            </NavListLink>
            {navButtons.map(itm => (
                <NavListButton
                    key={itm.name}
                    selected={drawerComponent === itm.value}
                    onClickHandler={() => handleClick(itm.value as DrawerComponents)}
                >
                    <>
                        <Tooltip title={itm.name} placement="top" disableInteractive>
                            {itm.icon}
                        </Tooltip>
                    </>
                </NavListButton>
            ))}
            <NavListLink navTarget="/activityReport" primaryColor={false}>
                <Tooltip title="Activity Report" placement="top" disableInteractive>
                    <AssessmentIcon />
                </Tooltip>
            </NavListLink>
            {!clientHasEngagePlatform && (
                <NavListLink navTarget="/calendar" primaryColor={false}>
                    <Tooltip title="Calendar" placement="top" disableInteractive>
                        <CalendarTodayIcon />
                    </Tooltip>
                </NavListLink>
            )}
        </List>
    );
}
